import { useState } from 'react';
import { Globe, ChevronDown, Check, Layers } from 'lucide-react';

interface Domain {
    id: number;
    domain: string;
    project_id?: number;
    network?: string;
    revshare?: number;
    is_active?: boolean;
}

export interface GroupOption {
    id: number;
    name: string;
    hostnames: string[]; // hostnames de TODOS os membros do grupo
}

interface CompactDomainSelectProps {
    value: string[];
    onChange: (val: string[]) => void;
    domains: Domain[] | undefined;
    groups?: GroupOption[]; // opcional — só renderizado se fornecido
}

export function CompactDomainSelect({ value, onChange, domains, groups }: CompactDomainSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [lastClickTime, setLastClickTime] = useState<{[key: string]: number}>({});

    const getSubdomains = (parentDomain: string) => {
        if (!domains) return [];

        return domains
            .map(d => d.domain)
            .filter(domain => {
                if (domain === parentDomain) return false;
                return domain.endsWith('.' + parentDomain);
            });
    };

    const handleToggleDomain = (domain: string) => {
        const now = Date.now();
        const lastClick = lastClickTime[domain] || 0;
        const timeDiff = now - lastClick;

        if (timeDiff < 300) {
            const subdomains = getSubdomains(domain);
            const allDomains = [domain, ...subdomains];
            const allSelected = allDomains.every(d => value.includes(d));

            if (allSelected) {
                onChange(value.filter(d => !allDomains.includes(d)));
            } else {
                const newSelection = [...value];
                allDomains.forEach(d => {
                    if (!newSelection.includes(d)) {
                        newSelection.push(d);
                    }
                });
                onChange(newSelection);
            }
        } else {
            if (value.includes(domain)) {
                onChange(value.filter(d => d !== domain));
            } else {
                onChange([...value, domain]);
            }
        }

        setLastClickTime(prev => ({
            ...prev,
            [domain]: now
        }));
    };

    const handleSelectAll = () => {
        if (domains) {
            onChange(domains.map(d => d.domain));
        }
    };

    const handleClearAll = () => {
        onChange([]);
    };

    const isGroupFullySelected = (group: GroupOption) =>
        group.hostnames.length > 0 && group.hostnames.every(h => value.includes(h));

    const handleToggleGroup = (group: GroupOption) => {
        if (group.hostnames.length === 0) return;
        if (isGroupFullySelected(group)) {
            // Remove todos os hostnames do grupo
            onChange(value.filter(d => !group.hostnames.includes(d)));
        } else {
            // Adiciona os que faltam (sem duplicar)
            const next = [...value];
            for (const h of group.hostnames) {
                if (!next.includes(h)) next.push(h);
            }
            onChange(next);
        }
    };

    const displayText = () => {
        if (value.length === 0) {
            return 'Todos os domínios';
        }
        if (value.length === 1) {
            return value[0];
        }
        if (value.length === domains?.length) {
            return 'Todos os domínios';
        }
        return `${value.length} domínios`;
    };

    const hasSubdomains = (domain: string) => {
        return getSubdomains(domain).length > 0;
    };

    return (
        <div className="relative w-full sm:w-auto">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full sm:w-auto flex items-center gap-2 px-3 py-1.5 bg-background border border-border rounded-md hover:border-foreground/40 transition-colors text-sm min-w-0 sm:min-w-[180px]"
            >
                <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="font-medium text-foreground flex-1 text-left truncate">
                    {displayText()}
                </span>
                {value.length > 0 && value.length < (domains?.length || 0) && (
                    <div className="flex items-center gap-1">
                        <span className="px-1.5 py-0.5 border border-border text-muted-foreground rounded text-xs font-semibold">
                            {value.length}
                        </span>
                    </div>
                )}
                <ChevronDown
                    className={`h-3.5 w-3.5 text-muted-foreground transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {isOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                    <div className="absolute top-full left-0 right-0 sm:right-auto mt-1 z-50 w-full sm:min-w-[280px] bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
                        {/* Header com ações */}
                        <div className="p-2 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                            <span className="text-xs font-semibold text-gray-600 uppercase">
                                Selecionar Domínios
                            </span>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={handleSelectAll}
                                    className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                                >
                                    Todos
                                </button>
                                <button
                                    onClick={handleClearAll}
                                    className="text-xs text-gray-600 hover:text-gray-700 font-medium px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                                >
                                    Limpar
                                </button>
                            </div>
                        </div>

                        {/* Dica de uso */}
                        <div className="px-3 py-2 bg-blue-50 border-b border-blue-100">
                            <p className="text-xs text-blue-700">
                                💡 <strong>Dica:</strong> Clique 2x em um domínio para selecionar ele + todos os subdomínios
                            </p>
                        </div>

                        {/* Lista de grupos (se fornecida) */}
                        {groups && groups.length > 0 && (
                            <div className="border-b border-gray-100">
                                <div className="px-3 pt-2 pb-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                                    Grupos
                                </div>
                                <div className="p-1 max-h-40 overflow-y-auto">
                                    {groups.map((g) => {
                                        const fullySelected = isGroupFullySelected(g);
                                        const partial =
                                            !fullySelected &&
                                            g.hostnames.some((h) => value.includes(h));
                                        return (
                                            <button
                                                key={`grp-${g.id}`}
                                                onClick={() => handleToggleGroup(g)}
                                                disabled={g.hostnames.length === 0}
                                                className={`w-full text-left px-3 py-2 rounded-md transition-colors text-sm flex items-center justify-between gap-2 ${
                                                    fullySelected
                                                        ? 'bg-violet-50 text-violet-700'
                                                        : partial
                                                            ? 'bg-violet-50/40 text-violet-700'
                                                            : 'hover:bg-gray-50 text-gray-700'
                                                } ${g.hostnames.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <Layers className="h-3.5 w-3.5 text-violet-500 flex-shrink-0" />
                                                    <span className="truncate font-medium">{g.name}</span>
                                                    <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
                                                        {g.hostnames.length}
                                                    </span>
                                                </div>
                                                {fullySelected && (
                                                    <Check className="h-4 w-4 text-violet-600 flex-shrink-0" />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Lista de domínios */}
                        <div className="px-3 pt-2 pb-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                            Domínios
                        </div>
                        <div className="p-1 max-h-64 overflow-y-auto">
                            {domains?.map((domainObj) => {
                                const isSelected = value.includes(domainObj.domain);
                                const hasSubs = hasSubdomains(domainObj.domain);
                                const subdomainsCount = getSubdomains(domainObj.domain).length;

                                return (
                                    <button
                                        key={domainObj.id}
                                        onClick={() => handleToggleDomain(domainObj.domain)}
                                        className={`w-full text-left px-3 py-2 rounded-md transition-colors text-sm flex items-center justify-between group ${
                                            isSelected
                                                ? 'bg-blue-50 text-blue-700'
                                                : 'hover:bg-gray-50 text-gray-700'
                                        }`}
                                    >
                                        <div className="flex-1 flex items-center gap-2 min-w-0">
                                            <span className="truncate">{domainObj.domain}</span>
                                            {hasSubs && (
                                                <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
                                                    +{subdomainsCount}
                                                </span>
                                            )}
                                        </div>
                                        {isSelected && (
                                            <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Footer com contador */}
                        {value.length > 0 && (
                            <div className="p-2 border-t border-gray-100 bg-gray-50">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-600">
                                        {value.length} de {domains?.length || 0} selecionados
                                    </span>
                                    <button
                                        onClick={() => setIsOpen(false)}
                                        className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                                    >
                                        Aplicar
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}